import React, { FC, useState, useCallback } from 'react'
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Fab,
} from '@mui/material'
import MedicationIcon from '@mui/icons-material/Medication'
import { MedChangeLog } from '../../types/models/MedChangeLog'
import { PlusIcon } from '../../icons/PlusIcon'
import { formatDateTimeToDate } from '../../helpers/formatDateTimeToDate'
import { BackButton } from '../../components/molecules/BackButton'
import { useMedChangeLogsQuery, useMedChangeModal } from '../../features/alert'
import { useToast } from '../../contexts/ToastContext'

export const MedChangePage: FC = () => {
  const [selectedMedChangeLog, setSelectedMedChangeLog] =
    useState<MedChangeLog>()
  const theme = useTheme()
  const { showToast } = useToast()

  const {
    data: medChangeLogs = [],
    isPending,
    error: queryError,
  } = useMedChangeLogsQuery()

  // クエリエラーを表示
  React.useEffect(() => {
    if (queryError) {
      showToast(
        'データの取得に失敗しました。再度お試しください。',
        'error',
        6000
      )
    }
  }, [queryError, showToast])

  const handleRowClick = useCallback(
    (medChangeLog: MedChangeLog, index: number) => {
      setSelectedMedChangeLog(medChangeLogs[index])
    },
    [medChangeLogs]
  )

  const { openModal: openNewModal, renderModal: renderNewModal } =
    useMedChangeModal()

  const { openModal: openEditModal, renderModal: renderEditModal } =
    useMedChangeModal(selectedMedChangeLog)

  return (
    <>
      {isPending && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <Typography>読み込み中...</Typography>
        </Box>
      )}
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: theme.palette.grey[50],
          py: 4,
          px: { xs: 2, sm: 4 },
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              gap: 2,
            }}
          >
            <MedicationIcon
              sx={{
                fontSize: 48,
                color: theme.palette.secondary.main,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.text.secondary,
                textAlign: 'center',
              }}
            >
              採用薬の一時変更登録
            </Typography>
          </Box>

          <Stack sx={{ pb: '80px' }}>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 'calc(100vh - 200px)',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a8a8a8',
                  },
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        minWidth: '120px',
                      }}
                    >
                      一般名
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        minWidth: '120px',
                      }}
                    >
                      変更前
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        minWidth: '120px',
                      }}
                    >
                      変更後
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        minWidth: '100px',
                      }}
                    >
                      変更通知開始日
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        minWidth: '120px',
                      }}
                    >
                      アラート終了日
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {medChangeLogs.map((item, index) => (
                    <TableRow
                      key={item.id}
                      onClick={() => {
                        handleRowClick(item, index)
                        openEditModal()
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          transition: 'all 0.2s ease-in-out',
                        },
                        '&:active': {
                          backgroundColor: theme.palette.action.selected,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <TableCell
                        sx={{
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.med_ippan_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.original_med_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.updated_med_name}
                      </TableCell>
                      <TableCell>
                        {formatDateTimeToDate(item.start_date)}
                      </TableCell>
                      <TableCell>
                        {formatDateTimeToDate(item.end_date)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {medChangeLogs.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 4, color: 'text.secondary' }}
                      >
                        データがありません
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Box>
      </Box>

      <Fab
        onClick={openNewModal}
        sx={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          transition: 'all 0.3s ease-in-out',
          color: '#fff',
          backgroundColor: theme.palette.primary.dark,
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            transform: 'scale(1.1)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <PlusIcon />
      </Fab>

      <BackButton
        text="管理ポータルに戻る"
        position="fixed"
        variant="bottomLeft"
      />

      {renderNewModal()}
      {renderEditModal()}
    </>
  )
}
